/**
 * API functions for lookup table management
 */
import apiClient from '@/lib/axios';
import type {
  LookupDto,
  CreateLookupRequest,
  UpdateLookupRequest,
  ReorderLookupsRequest,
} from '../types/lookup';

// Backend wraps responses in Result<T> type
interface ApiResult<T> {
  success: boolean;
  data: T;
  message?: string;
}

/**
 * Fetch all lookup items for a given type
 * @param type - The lookup type (damage-types, referral-types, etc.)
 * @param includeInactive - Whether to include inactive items
 */
export const getLookups = async (
  type: string,
  includeInactive: boolean = false
): Promise<LookupDto[]> => {
  const response = await apiClient.get<ApiResult<LookupDto[]>>(`/lookups/${type}`, {
    params: { includeInactive },
  });
  return response.data.data;
};

/**
 * Create a new lookup item
 * @param type - The lookup type
 * @param data - The lookup item data
 */
export const createLookup = async (
  type: string,
  data: CreateLookupRequest
): Promise<LookupDto> => {
  const response = await apiClient.post<ApiResult<LookupDto>>(`/lookups/${type}`, data);
  return response.data.data;
};

/**
 * Update an existing lookup item
 * @param type - The lookup type
 * @param id - The lookup item ID
 * @param data - The updated data
 */
export const updateLookup = async (
  type: string,
  id: number,
  data: UpdateLookupRequest
): Promise<LookupDto> => {
  const response = await apiClient.put<ApiResult<LookupDto>>(`/lookups/${type}/${id}`, data);
  return response.data.data;
};

/**
 * Delete (deactivate) a lookup item
 * @param type - The lookup type
 * @param id - The lookup item ID
 */
export const deleteLookup = async (type: string, id: number): Promise<void> => {
  await apiClient.delete(`/lookups/${type}/${id}`);
};

/**
 * Reorder lookup items
 * @param type - The lookup type
 * @param ids - Array of IDs in the new order
 */
export const reorderLookups = async (
  type: string,
  ids: number[]
): Promise<void> => {
  const payload: ReorderLookupsRequest = { ids };
  await apiClient.post(`/lookups/${type}/reorder`, payload);
};
